/**
 * Store d'authentification — SOURCE UNIQUE DU RÔLE (spec §5.2).
 *
 * Le rôle vient exclusivement de `GET /api/me`. Aucun décodage de JWT côté
 * navigateur (`app/utils/jwt.ts` n'est PAS porté) : `tokenClaims()` ne sert
 * qu'au nom d'affichage tant que `/api/me` n'a pas répondu.
 */
import { computed, ref } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { defineStore } from 'pinia';
import { useQueryCache } from '@pinia/colada';
import { AppRole } from '@/shared/enums';
import { ErrorCode } from '@/api/generated/types.gen';
import type { CurrentUser } from '@/api/generated/types.gen';
import { getMe } from '@/api/generated/sdk.gen';
import { isApiError } from '@/api/errors';
import { apiErrorMessage } from '@/api/messages';
import { revokeAuthenticatedObjectUrls } from '@/api/authFiles';
import { payload } from '@/api/unwrap';
import { appRoleFromValue } from './roles';
import { isAuthenticated, login, logout, tokenClaims } from './keycloak';

export type AuthStatus = 'idle' | 'loading' | 'ready' | 'error';

function claimString(claims: Record<string, unknown> | undefined, key: string): string {
  const value = claims?.[key];
  return typeof value === 'string' ? value : '';
}

export const useAuthStore = defineStore('auth', () => {
  const me: Ref<CurrentUser | null> = ref<CurrentUser | null>(null);
  const status: Ref<AuthStatus> = ref<AuthStatus>('idle');
  const error: Ref<string | null> = ref<string | null>(null);

  /** Chargement en vol — hors réactivité : `ensureMe()` est ré-entrante. */
  let inflight: Promise<void> | null = null;

  const role: ComputedRef<AppRole | null> = computed(() => appRoleFromValue(me.value?.role));
  const clientId: ComputedRef<string | null> = computed(() => me.value?.clientId ?? null);

  const isAdmin = computed(() => role.value === AppRole.ADMIN);
  const isAmesura = computed(() => role.value === AppRole.AMESURA);
  const isClient = computed(() => role.value === AppRole.CLIENT);
  const isInternal = computed(() => isAdmin.value || isAmesura.value);

  const firstName = computed<string>(() => {
    if (me.value?.firstName) return me.value.firstName;
    const claims = tokenClaims();
    return claimString(claims, 'given_name')
      || me.value?.login
      || claimString(claims, 'preferred_username');
  });

  const lastName = computed<string>(() => {
    if (me.value?.lastName) return me.value.lastName;
    return claimString(tokenClaims(), 'family_name');
  });

  const displayName = computed<string>(() => {
    const full = [firstName.value, lastName.value].filter(Boolean).join(' ').trim();
    if (full) return full;
    const claims = tokenClaims();
    return me.value?.login || claimString(claims, 'preferred_username') || claimString(claims, 'email');
  });

  const initials = computed<string>(() => {
    const letters = [firstName.value, lastName.value]
      .map((part) => part.trim().charAt(0))
      .filter(Boolean)
      .join('');
    const fallback = displayName.value.trim().slice(0, 2);
    return (letters || fallback || '··').toUpperCase();
  });

  function reset(): void {
    me.value = null;
    status.value = 'idle';
    error.value = null;
    inflight = null;
  }

  async function load(): Promise<void> {
    status.value = 'loading';
    error.value = null;
    try {
      me.value = await payload(getMe({ throwOnError: true }));
      status.value = 'ready';
    } catch (cause) {
      if (isApiError(cause) && cause.code === ErrorCode.AUTH_NO_VALID_ROLE) {
        // Compte sans rôle applicatif : ce n'est PAS une erreur technique, le
        // guard route vers /forbidden.
        me.value = null;
        status.value = 'ready';
        return;
      }

      if (isApiError(cause) && cause.statusCode === 401) {
        // État TERMINAL dans tous les cas : sans lui `status` resterait sur
        // 'loading' et le guard enverrait sur /forbidden.
        me.value = null;
        status.value = 'error';

        // L'intercepteur a déjà tenté refresh forcé + rejeu. Encore authentifié =
        // jeton FRAIS refusé (compte supprimé, claims manquants, `iss` divergent) :
        // `login()` reviendrait aussitôt via la session SSO — boucle infinie.
        if (isAuthenticated()) {
          error.value = 'Le serveur refuse votre session. Déconnectez-vous puis reconnectez-vous ; '
            + 'si le problème persiste, contactez un administrateur.';
          return;
        }

        error.value = 'Session expirée — reconnexion…';
        void login();
        return;
      }

      me.value = null;
      error.value = apiErrorMessage(cause, 'Impossible de charger votre profil. Réessayez dans un instant.');
      status.value = 'error';
    }
  }

  /** Idempotent : charge `GET /api/me` une seule fois, appels concurrents partagés. */
  async function ensureMe(): Promise<void> {
    if (status.value === 'ready') return;
    if (inflight) return inflight;

    const pending = load();
    inflight = pending;
    try {
      await pending;
    } finally {
      if (inflight === pending) inflight = null;
    }
  }

  /**
   * Déconnexion.
   *
   * ⚠️ ORDRE : le cache de requêtes d'abord, `logout()` ensuite, `reset()` en
   * dernier. Un `reset()` AVANT la redirection laisserait le store en `'idle'`
   * alors que le jeton Keycloak est encore valide : `ensureMe()` se réarme, et
   * la moindre navigation (ou une redirection bloquée / annulée) referait un
   * `GET /api/me` qui « reconnecte » silencieusement l'UI, cache intact.
   */
  async function signOut(): Promise<void> {
    const cache = useQueryCache();
    cache.getEntries().forEach((entry) => cache.remove(entry));
    revokeAuthenticatedObjectUrls();
    await logout();
    reset();
  }

  return {
    me,
    status,
    error,
    role,
    clientId,
    isAdmin,
    isAmesura,
    isClient,
    isInternal,
    displayName,
    firstName,
    initials,
    ensureMe,
    reset,
    signOut,
  };
});
